<template>
  <section class="content">
    <section class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">

          <div class="col-sm-6">
            <h1>Detalles de Sock: {{ producto.descripcion }}</h1>
          </div>

          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
              <li class="breadcrumb-item active">Movimiento</li>
            </ol>
          </div>
        </div>
      </div>
    </section>
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h3 class="card-title">Disponibilidad por almacen:</h3>
            </div>
            <div class="card-body">
              <table class="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Almacén</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in stock" :key="item.almacen.id">
                    <td>{{ item.almacen.descripcion }}</td>
                    <td>{{ calcularCJyUN(item.cantidad) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total disponible:</th>
                    <th><strong>{{ cajasYunidadesTot }}</strong></th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div class="card">
            <div class="card-header">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="card-title">HISTORIAL DE MOVIMIENTOS</h3>
                </div>

                <!-- Botón dropdown de filtros -->
                <div class="col-auto">
                  <div class="container-fluid mb-3">
                    <div class="dropdown d-inline-block">
                      <button ref="toggleFiltros" class="btn btn-info dropdown-toggle" type="button" id="ddlFiltros"
                        data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="fas fa-filter"></i> Filtros
                      </button>

                      <div class="dropdown-menu p-3 dropdown-menu-right" aria-labelledby="ddlFiltros"
                        style="min-width: 280px;" @click.stop>
                        <div class="form-group">
                          <label>Almacén</label>
                          <select v-model="almacenSeleccionado" class="form-control">
                            <option value="">Todos</option>
                            <option v-for="alm in stock" :key="alm.id" :value="alm.id">
                              {{ alm.almacen.descripcion }}
                            </option>
                          </select>
                        </div>

                        <div class="form-group">
                          <label>Tipo de movimiento</label>
                          <select v-model="tipoSeleccionado" class="form-control">
                            <option value="">Todos</option>
                            <option value="entrada">Entrada</option>
                            <option value="salida">Salida</option>
                            <option value="traslado">Traslado</option>
                          </select>
                        </div>

                        <div class="form-group">
                          <label>Fecha desde</label>
                          <input type="date" v-model="fechaDesde" class="form-control" />
                        </div>

                        <div class="form-group">
                          <label>Fecha hasta</label>
                          <input type="date" v-model="fechaHasta" class="form-control" />
                        </div>

                        <div class="d-flex justify-content-between mt-2">
                          <button class="btn btn-sm btn-outline-secondary" @click="limpiarFiltros">
                            Limpiar
                          </button>
                          <button class="btn btn-sm btn-primary" @click="$refs.toggleFiltros.click()">
                            Cerrar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-body">
              <table id="tablaMovimientos" class="table table-bordered table-striped">
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import api from '@/services/api'

// recibimos el ID del producto como prop
const props = defineProps({
  productoId: {
    type: [String, Number],
    required: true
  }
})

// datos crudos
const producto = ref({})
const stock = ref([])
const movimientos = ref([])
let cajasTotales = 0
let unidadesTotales = 0
let cajasYunidadesTot 

// filtros
const tipoSeleccionado = ref('')
const almacenSeleccionado = ref('')
const fechaDesde = ref('')
const fechaHasta = ref('')

// DataTable instance
const dtInstance = ref(null)

/**
 * Función auxiliar para formatear cualquier cantidad numérica en CJ y UN conservando el signo y estilo visual
 */
function formatearCantidad(cantidadNum, tipoMovimiento = null) {
  const cantxcaja = Number(producto.value.cantxcaja) || 1
  
  if (cantidadNum === 0) return '<span class="text-muted">0</span>'

  const esNegativo = cantidadNum < 0
  const absCant = Math.abs(cantidadNum)

  const cajas = Math.trunc(absCant / cantxcaja)
  const unidades = absCant % cantxcaja

  let texto = ''
  if (cajas !== 0) {
    texto = `${cajas}CJ`
    if (unidades !== 0) texto += ` + ${unidades}UN`
  } else {
    texto = `${unidades}UN`
  }

  // Si evaluamos la columna de Movimiento, aplicamos color e iconos según el tipo
  if (tipoMovimiento) {
    if (tipoMovimiento === 'entrada') {
      return `<span class="text-success font-weight-bold"><i class="fas fa-arrow-down"></i> + ${texto}</span>`
    } else if (tipoMovimiento === 'salida') {
      return `<span class="text-danger font-weight-bold"><i class="fas fa-arrow-up"></i> - ${texto}</span>`
    } else if (tipoMovimiento === 'traslado') {
      return `<span class="text-info font-weight-bold"><i class="fas fa-exchange-alt"></i> ${texto}</span>`
    }
  }

  // Para otras columnas (como estatus) solo mostramos signo si es negativo
  if (esNegativo) {
    return `<span class="text-danger font-weight-bold">- ${texto}</span>`
  }

  return texto
}

/**
 * Computed: aplica los cuatro filtros sobre movimientos
 */
const movimientosFiltrados = computed(() => {
  const desde = fechaDesde.value ? new Date(fechaDesde.value) : null
  const hasta = fechaHasta.value ? new Date(fechaHasta.value) : null
  const tipo = tipoSeleccionado.value
  const almId = almacenSeleccionado.value
    ? Number(almacenSeleccionado.value)
    : null

  return movimientos.value.filter(m => {
    if (tipo && m.tipo !== tipo) return false

    if (almId !== null) {
      if (m.origen_id !== almId && m.destino_id !== almId) return false
    }

    const f = new Date(m.fecha)
    if (desde && f < desde) return false
    if (hasta && f > hasta) return false

    return true
  })
})

/**
 * Inicializa DataTable con los datos filtrados
 */
async function initTable() {
  await nextTick()
  dtInstance.value = $('#tablaMovimientos').DataTable({
    data: movimientosFiltrados.value,
    columns: [
      { data: 'numcontrol', title: 'Nº Control' },
      { data: 'fecha', title: 'Fecha' },
      { data: 'almacenTexto', title: 'Almacén' },
      { 
        data: 'cantidadMovimientoTexto', 
        title: 'Movimiento' 
      },
      { 
        data: 'estatusAcumuladoTexto', 
        title: 'Estatus' 
      },
      { data: 'motivo', title: 'Motivo' },
      {
        data: null,
        title: 'Acciones',
        className: 'text-center',
        render: row => `
          <a
            href="/movimientos/${row.tipo}/${row.numcontrol}"
            class="btn btn-sm btn-info"
          >
            Ver detalles
          </a>`
      }
    ],
    order: [[1, 'desc']],
    responsive: true,
    autoWidth: false,
    pageLength: 10,
    lengthChange: false,
    pagingType: 'numbers',
    language: { url: 'https://cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json' },
    dom:
      '<"row mb-3"<"col-12 col-md-6"f>>' +
      't' +
      '<"row mt-3"<"col-12 text-center"i>>' +
      '<"row"<"col-12 d-flex justify-content-center"p>>'
  })
}

/**
 * Limpia y actualiza filas sin reinicializar la tabla
 */
function updateTable() {
  dtInstance.value
    .clear()
    .rows.add(movimientosFiltrados.value)
    .draw(false)
}

function limpiarFiltros() {
  almacenSeleccionado.value = ''
  tipoSeleccionado.value = ''
  fechaDesde.value = ''
  fechaHasta.value = ''
}

function calcularCJyUN(i) {
  const cantxcaja = Number(producto.value.cantxcaja);
  const cantidad = i;
  if (cantidad === 0) {
    cajasYunidadesTot = '0' 
    return 0
  }
  const cajas = Math.trunc(cantidad / cantxcaja);
  const unidades = cantidad % cantxcaja;
  let cajasYunidades = '';

  if (cajas !== 0) {
    cajasYunidades = `${cajas}CJ`;
    if (unidades !== 0) {
      cajasYunidades += ` + ${unidades}UN`;
    }
  } else {
    cajasYunidades = `${unidades}UN`;
  }
  cajasTotales = cajasTotales + cajas;
  unidadesTotales = unidadesTotales + unidades;

  if (cajasTotales !== 0) {
    cajasYunidadesTot = `${cajasTotales}CJ`;
    if (unidades !== 0) {
      cajasYunidadesTot += ` + ${unidadesTotales}UN`;
    }
  } else {
    cajasYunidadesTot = `${unidadesTotales}UN`;
  }
  return cajasYunidades;
}

/**
 * Al montar, cargamos datos, procesamos el historial acumulado y establecemos el DataTable
 */
onMounted(async () => {
  const [
    prodRes,
    entRes,
    salRes,
    traRes,
    invRes
  ] = await Promise.all([
    api.get(`/producto/${props.productoId}`),
    api.get(`/entradas/producto/${props.productoId}`),
    api.get(`/salidas/producto/${props.productoId}`),
    api.get(`/traslados/producto/${props.productoId}`),
    api.get(`/inventarios/producto/${props.productoId}`)
  ])

  // detalle y stock
  producto.value = prodRes.data
  stock.value = invRes.data

  // Mapeo unificado de movimientos
  const entradas = entRes.data.map(e => ({
    id: e.id,
    numcontrol: e.numcontrol,
    fecha: e.fecha,
    tipo: 'entrada',
    valorCantidad: Number(e.cantidad),
    impactoAcumulado: Number(e.cantidad),
    almacenTexto: e.almacen.descripcion,
    origen_id: null,
    destino_id: e.almacen.id,
    motivo: e.motivo.nombre
  }))

  const salidas = salRes.data.map(s => ({
    id: s.id,
    numcontrol: s.numcontrol,
    fecha: s.fecha,
    tipo: 'salida',
    valorCantidad: Number(s.cantidad),
    impactoAcumulado: -Math.abs(Number(s.cantidad)),
    almacenTexto: s.almacen.descripcion,
    origen_id: s.almacen.id,
    destino_id: null,
    motivo: s.motivo.nombre
  }))

  const traslados = traRes.data.map(t => ({
    id: t.id,
    numcontrol: t.numcontrol,
    fecha: t.fecha,
    tipo: 'traslado',
    valorCantidad: Number(t.cantidad),
    impactoAcumulado: 0,
    almacenTexto: `${t.almacen_origen.descripcion} => ${t.almacen_destino.descripcion}`,
    origen_id: t.almacen_origen.id,
    destino_id: t.almacen_destino.id,
    motivo: t.motivo.nombre
  }))

  let todosLosMovimientos = [...entradas, ...salidas, ...traslados]

  // Ordenar cronológicamente (más antiguo a más nuevo) para calcular el acumulado correctamente
  todosLosMovimientos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha))

  let acumuladoStock = 0
  movimientos.value = todosLosMovimientos.map(m => {
    acumuladoStock += m.impactoAcumulado

    return {
      ...m,
      cantidadMovimientoTexto: formatearCantidad(m.valorCantidad, m.tipo),
      estatusAcumuladoTexto: formatearCantidad(acumuladoStock, null)
    }
  })
})

/**
 * Cada vez que cambien los filtros o los datos filtrados,
 * redibuja filas nuevas
 */
watch(
  movimientosFiltrados,
  () => {
    if (dtInstance.value) { updateTable() } else { initTable() }
  }
)
</script>